import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  getNotifications,
  getUnreadCount,
  getMyMintCredits,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  loginWithEmail,
  registerWithEmail,
} from '../../services/api'
import { formatAddress } from '../../services/wallet'
import { formatDateTime } from '../../utils/formatters'
import {
  resolveNotificationPath,
  ownerFocusCardIdFromNotification,
} from '../../utils/notificationNav'
import { STORE_MENU, STORE_ORIGIN, STORE_SOCIALS } from '../../config/storeLinks'
import { useAuthSession } from '../../contexts/AuthSessionContext'
import EmailAuthModal from '../EmailAuthModal'
import InstallAppButton from '../../pwa/InstallAppButton'
import InstallAppItem from '../../pwa/InstallAppItem'
import { MINT_CREDITS_EVENT } from '../../utils/mintCreditsSync'

const ICON_BUTTON =
  'inline-flex h-11 w-11 items-center justify-center text-white transition-colors hover:text-m1-violet'

const OWNER_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/owner', label: 'My Cards' },
  { to: '/profile', label: 'Profile' },
]

function HamburgerIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 32 32"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M0 26.667h32M0 16h26.98M0 5.333h32" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  )
}

function AccountIcon() {
  return (
    <svg
      width="18"
      height="20"
      viewBox="0 0 14 18"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        d="M7.34497 10.0933C4.03126 10.0933 1.34497 12.611 1.34497 15.7169C1.34497 16.4934 1.97442 17.1228 2.75088 17.1228H11.9391C12.7155 17.1228 13.345 16.4934 13.345 15.7169C13.345 12.611 10.6587 10.0933 7.34497 10.0933Z"
        stroke="currentColor"
      />
      <ellipse
        cx="7.34503"
        cy="5.02631"
        rx="3.63629"
        ry="3.51313"
        stroke="currentColor"
        strokeLinecap="square"
      />
    </svg>
  )
}

/**
 * Slim fixed header shared with m1ntaverse.com / Morpheus: menu drawer on the
 * left, logo in the middle, notifications and account on the right.
 */
export default function OwnerHeader() {
  const {
    user,
    wallet,
    emailAuthToken,
    isAuthenticated,
    needsEmailOnboarding,
    setAuthSession,
    logout,
  } = useAuthSession()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [notificationsOpen, setNotificationsOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  /** null until the first answer from the server, so the menu never shows a made-up 0. */
  const [mintCredits, setMintCredits] = useState(null)
  const [showAuthModal, setShowAuthModal] = useState(false)
  const toggleRef = useRef(null)
  const closeRef = useRef(null)
  const notificationsRef = useRef(null)

  const showUserMenu = Boolean(isAuthenticated && !needsEmailOnboarding)
  const notifyWallet = wallet || null

  const closeMenu = useCallback(() => {
    setMenuOpen(false)
    toggleRef.current?.focus()
  }, [])

  // Drawer: lock page scroll, focus the close button, close on Escape.
  useEffect(() => {
    if (!menuOpen) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    closeRef.current?.focus()
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeMenu()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen, closeMenu])

  useEffect(() => {
    const handleProfileUpdate = (event) => {
      if (event.detail?.user) setAuthSession(event.detail.user)
    }
    window.addEventListener('profilePictureUpdated', handleProfileUpdate)
    window.addEventListener('profileUpdated', handleProfileUpdate)
    return () => {
      window.removeEventListener('profilePictureUpdated', handleProfileUpdate)
      window.removeEventListener('profileUpdated', handleProfileUpdate)
    }
  }, [])

  useEffect(() => {
    const handleOpenAuthModal = () => {
      setMenuOpen(false)
      setShowAuthModal(true)
    }
    window.addEventListener('openAuthModal', handleOpenAuthModal)
    return () => window.removeEventListener('openAuthModal', handleOpenAuthModal)
  }, [])

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!wallet && !emailAuthToken) {
      setUnreadCount(0)
      setNotifications([])
      return undefined
    }
    const fetchUnread = () => {
      getUnreadCount(notifyWallet)
        .then((res) => setUnreadCount(res.data?.data?.count ?? 0))
        .catch(() => {})
    }
    fetchUnread()
    const interval = setInterval(fetchUnread, 30000)
    return () => clearInterval(interval)
  }, [wallet, emailAuthToken])

  // Real balance from the server: on login, when the drawer opens, when the tab regains focus, every
  // 30 seconds, and instantly when a page announces a fresh value.
  useEffect(() => {
    if (!wallet && !emailAuthToken) {
      setMintCredits(null)
      return undefined
    }
    let cancelled = false
    const refreshCredits = () => {
      getMyMintCredits(notifyWallet)
        .then((res) => {
          if (!cancelled) setMintCredits(Number(res.data?.data?.mintCredits || 0))
        })
        .catch(() => {})
    }
    const onAnnounced = (event) => {
      if (Number.isFinite(event.detail?.balance)) setMintCredits(event.detail.balance)
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') refreshCredits()
    }
    refreshCredits()
    const interval = setInterval(refreshCredits, 30000)
    window.addEventListener(MINT_CREDITS_EVENT, onAnnounced)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener(MINT_CREDITS_EVENT, onAnnounced)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [wallet, emailAuthToken, menuOpen])

  const fetchNotifications = () => {
    if (!wallet && !emailAuthToken) return
    getNotifications(notifyWallet)
      .then((res) => setNotifications(res.data?.data ?? []))
      .catch(() => setNotifications([]))
  }

  const finishLogin = (d) => {
    setAuthSession({
      user: d.user,
      emailAuthToken: d.emailAuthToken ?? null,
      onboardingToken: null,
    })
    setShowAuthModal(false)
    navigate('/owner')
  }

  const handleEmailLogin = async ({ email, password }) => {
    try {
      const res = await loginWithEmail({ email, password })
      finishLogin(res.data)
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Login failed. Please try again.'
      const code = err.response?.data?.code
      const error = new Error(msg)
      if (code) error.code = code
      throw error
    }
  }

  const handleEmailRegister = async (data) => {
    try {
      const res = await registerWithEmail(data)
      finishLogin(res.data)
    } catch (err) {
      const msg =
        err.response?.data?.error || err.message || 'Registration failed. Please try again.'
      throw new Error(msg)
    }
  }

  const handleDisconnect = () => {
    logout()
    setShowAuthModal(false)
    setMenuOpen(false)
    navigate('/')
  }

  const openNotification = (notification) => {
    const path = resolveNotificationPath(notification)
    const focusCardId = ownerFocusCardIdFromNotification(notification, path)
    setNotificationsOpen(false)
    if (focusCardId) {
      navigate('/owner', { state: { fromNotification: true, openOwnerCardId: focusCardId } })
    } else if (path) {
      navigate(path)
    }
    if (!notification.read && (wallet || emailAuthToken)) {
      markNotificationAsRead(notification.id, notifyWallet)
        .then(() => {
          setUnreadCount((c) => Math.max(0, c - 1))
          setNotifications((prev) =>
            prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
          )
        })
        .catch((e) => console.error(e))
    }
  }

  const displayName = user?.name || (wallet ? formatAddress(wallet) : user?.email || '')

  return (
    <>
      <header className="fixed top-0 z-50 w-full border-b border-[#272C32] bg-[#18181A] pt-[env(safe-area-inset-top,0px)]">
        <div className="grid h-14 w-full grid-cols-[1fr_auto_1fr] items-center px-[calc(var(--gutter)-10px)]">
          <div className="flex items-center justify-start">
            <button
              ref={toggleRef}
              type="button"
              onClick={() => setMenuOpen(true)}
              aria-label="Menu"
              aria-haspopup="dialog"
              aria-expanded={menuOpen}
              className={ICON_BUTTON}
            >
              <HamburgerIcon />
            </button>
          </div>

          <Link to="/" aria-label="M1NT home" className="inline-flex items-center">
            <img
              src="/brand/m1nt-logo.png"
              alt="M1NT"
              draggable={false}
              className="block h-[17px] w-auto select-none"
            />
          </Link>

          <div className="flex items-center justify-end">
            <InstallAppButton />
            {showUserMenu && (
              <div className="relative" ref={notificationsRef}>
                <button
                  type="button"
                  onClick={() => {
                    if (!notificationsOpen) fetchNotifications()
                    setNotificationsOpen(!notificationsOpen)
                  }}
                  aria-label="Notifications"
                  aria-expanded={notificationsOpen}
                  className={`relative ${ICON_BUTTON}`}
                >
                  <i className="ri-notification-3-line text-xl" aria-hidden="true" />
                  {unreadCount > 0 && (
                    <span className="absolute right-1 top-1 flex h-[16px] min-w-[16px] items-center justify-center bg-m1-violet px-1 text-[10px] font-medium text-m1-ground">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {notificationsOpen && (
                  <div className="absolute right-0 top-full mt-2 max-h-[min(70vh,420px)] w-[min(92vw,360px)] overflow-y-auto border border-[#272C32] bg-[#18181A] shadow-[0_24px_48px_rgba(0,0,0,0.5)]">
                    <div className="flex items-center justify-between border-b border-[#272C32] px-4 py-3">
                      <h3 className="text-[14px] font-normal text-m1-muted">Notifications</h3>
                      {unreadCount > 0 && (
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await markAllNotificationsAsRead(notifyWallet)
                              setUnreadCount(0)
                              fetchNotifications()
                            } catch (e) {
                              console.error(e)
                            }
                          }}
                          className="text-[13px] text-m1-violet transition-colors hover:text-m1-ink"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>
                    {notifications.length === 0 ? (
                      <p className="px-4 py-8 text-center text-[15px] font-light text-m1-muted">
                        No notifications
                      </p>
                    ) : (
                      <ul>
                        {notifications.map((notification) => (
                          <li
                            key={notification.id}
                            className="border-b border-[#272C32] last:border-b-0"
                          >
                            <button
                              type="button"
                              onClick={() => openNotification(notification)}
                              className={`block w-full px-4 py-3 text-left transition-colors hover:bg-white/5 ${
                                notification.read ? '' : 'bg-m1-violet/10'
                              }`}
                            >
                              <p
                                className={`text-[15px] ${notification.read ? 'font-light text-m1-ink-soft' : 'font-normal text-m1-ink'}`}
                              >
                                {notification.title}
                              </p>
                              <p className="mt-0.5 text-[14px] font-light text-m1-muted">
                                {notification.message}
                              </p>
                              <p className="mt-1 font-m1-mono text-[12px] text-m1-muted">
                                {formatDateTime(notification.createdAt)}
                              </p>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}

            {showUserMenu ? (
              <Link
                to="/profile"
                aria-label="Profile"
                className="inline-flex h-11 items-center gap-2 pl-2 pr-[10px] text-white transition-colors hover:text-m1-violet"
              >
                <AccountIcon />
                <span className="hidden max-w-[120px] truncate text-[14px] tracking-[0.03em] sm:inline">
                  {displayName || 'Profile'}
                </span>
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => setShowAuthModal(true)}
                className="inline-flex h-11 items-center gap-2 pl-2 pr-[10px] text-white transition-colors hover:text-m1-violet"
              >
                <AccountIcon />
                <span className="text-[14px] tracking-[0.03em]">Log in</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {menuOpen && (
        <div className="fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label="Menu">
          <div
            onClick={closeMenu}
            className="m1nt-drawer-backdrop absolute inset-0 bg-black/70 backdrop-blur-[2px]"
            aria-hidden="true"
          />

          <nav
            className="m1nt-drawer-panel absolute inset-y-0 left-0 flex w-[min(88vw,400px)] flex-col overflow-y-auto border-r border-[#272C32] bg-[#18181A]"
            aria-label="M1NT"
          >
            <div className="flex h-14 shrink-0 items-center px-[calc(var(--gutter)-10px)]">
              <button
                ref={closeRef}
                type="button"
                onClick={closeMenu}
                aria-label="Close menu"
                className={ICON_BUTTON}
              >
                <i className="ri-close-line text-2xl" aria-hidden="true" />
              </button>
            </div>

            <p className="px-[var(--gutter)] pt-4 text-[14px] text-m1-muted">Owner portal</p>
            <ul className="px-[var(--gutter)] pt-2">
              {OWNER_LINKS.map((item) => {
                const active = location.pathname === item.to
                return (
                  <li key={item.to} className="border-b border-[#272C32]">
                    <Link
                      to={item.to}
                      onClick={() => setMenuOpen(false)}
                      aria-current={active ? 'page' : undefined}
                      className={`flex min-h-[52px] items-center py-2 text-[19px] font-light leading-snug transition-colors hover:text-m1-violet ${
                        active ? 'text-m1-violet' : 'text-m1-ink'
                      }`}
                    >
                      {item.label}
                    </Link>
                  </li>
                )
              })}
            </ul>

            <p className="px-[var(--gutter)] pt-8 text-[14px] text-m1-muted">M1NT store</p>
            <ul className="px-[var(--gutter)] pt-2">
              {STORE_MENU.map((item) => (
                <li key={item.href} className="border-b border-[#272C32]">
                  <a
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-[52px] items-center py-2 text-[19px] font-light leading-snug text-m1-ink transition-colors hover:text-m1-violet"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>

            <InstallAppItem />

            <div className="mt-auto px-[var(--gutter)] pb-8 pt-10">
              {showUserMenu ? (
                <>
                  <p className="text-[16px] text-m1-ink">{displayName}</p>
                  <p className="mt-1 text-[14px] text-m1-muted">
                    M1NT credits: {mintCredits === null ? '…' : mintCredits}
                  </p>
                  <button
                    type="button"
                    onClick={handleDisconnect}
                    className="mt-4 inline-flex min-h-[44px] items-center gap-3 text-[16px] text-m1-ink transition-colors hover:text-m1-violet"
                  >
                    <i className="ri-logout-box-r-line text-xl" aria-hidden="true" />
                    Log out
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setMenuOpen(false)
                    setShowAuthModal(true)
                  }}
                  className="inline-flex min-h-[44px] items-center gap-3 text-[16px] text-m1-ink transition-colors hover:text-m1-violet"
                >
                  <AccountIcon />
                  Log in
                </button>
              )}
              <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-3">
                {STORE_SOCIALS.map((social) => (
                  <li key={social.label}>
                    <a
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.label}
                      className="text-m1-muted transition-colors hover:text-m1-violet"
                    >
                      <i className={`${social.icon} text-xl`} aria-hidden="true" />
                    </a>
                  </li>
                ))}
              </ul>
              <a
                href={`${STORE_ORIGIN}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-block text-[14px] text-m1-muted transition-colors hover:text-m1-violet"
              >
                m1ntaverse.com
              </a>
            </div>
          </nav>
        </div>
      )}

      <EmailAuthModal
        variant="m1nt"
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLogin={handleEmailLogin}
        onRegister={handleEmailRegister}
        onFirstAccessComplete={finishLogin}
      />
    </>
  )
}
